import { Link } from "react-router-dom";
import { Shield, Clock, MessageCircle, Users, Play, FileText, Send, ChevronRight, CheckCircle, Video } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const CALL_URL = "https://cal.com/mathis-skillsview/30min?overlayCalendar=true";

const LANDING_COPY = {
  fr: {
    hero: {
      badge: "Analyse Vidéo Professionnelle",
      titleTop: "Passez au niveau",
      titleAccent: "supérieur",
      subtitle: "Analyses vidéo tactiques et techniques personnalisées pour votre équipe ou vos performances individuelles. Livraison sous 4 jours.",
      order: "Commander une analyse",
      start: "Commencer maintenant",
      how: "Comment ça marche",
      bookCall: "Réserver un appel",
    },
    trustBadges: {
      delivery: "Livraison 4 jours max",
      payment: "Paiement sécurisé",
      support: "Support WhatsApp",
    },
    process: {
      eyebrow: "Processus",
      title: "Comment ça marche",
      steps: [
        { id: "step-1", step: "01", icon: Play, title: "Envoyez votre vidéo", desc: "Uploadez votre match via SwissTransfer et partagez le lien avec nous." },
        { id: "step-2", step: "02", icon: FileText, title: "Décrivez vos besoins", desc: "Précisez les aspects tactiques ou techniques que vous souhaitez analyser." },
        { id: "step-3", step: "03", icon: Send, title: "Recevez votre analyse", desc: "Votre analyse personnalisée est livrée sous 4 jours maximum." },
      ],
    },
    pricing: {
      eyebrow: "Tarifs",
      title: "Nos formules",
      popular: "Populaire",
      subscribe: "S'abonner",
      choose: "Choisir",
      cards: [
        {
          testId: "pricing-collective",
          title: "Match Collectif",
          description: "Analyse tactique de votre équipe",
          price: 100,
          unit: "/ match",
          features: ["Analyse tactique complète", "Points forts et axes d'amélioration", "Livraison sous 4 jours"],
        },
        {
          testId: "pricing-individual",
          title: "Match Individuel",
          description: "Analyse de vos performances personnelles",
          price: 100,
          unit: "/ match",
          features: ["Focus sur votre jeu", "Conseils personnalisés", "Livraison sous 4 jours"],
        },
        {
          testId: "pricing-subscription",
          title: "Abonnement",
          description: "Pour un suivi régulier",
          price: 300,
          unit: "/ mois",
          features: ["4 analyses par mois", "Collectif ou individuel", "Support prioritaire"],
          popular: true,
        },
      ],
    },
    trust: [
      { id: "trust-delai", icon: Clock, title: "Délai garanti", desc: "Livraison sous 4 jours maximum ou remboursé" },
      { id: "trust-paiement", icon: Shield, title: "Paiement sécurisé", desc: "Transactions protégées par Stripe" },
      { id: "trust-support", icon: MessageCircle, title: "Support réactif", desc: "Contact WhatsApp pour toute question" },
      { id: "trust-humain", icon: Users, title: "Analyse humaine", desc: "Expertise réelle, pas d'IA générique" },
    ],
    cta: {
      title: "Prêt à progresser ?",
      subtitle: "Commandez votre première analyse et découvrez les clés de votre progression.",
      order: "Commander maintenant",
      create: "Créer mon compte",
    },
  },
  en: {
    hero: {
      badge: "Professional Video Analysis",
      titleTop: "Reach the next",
      titleAccent: "level",
      subtitle: "Personalized tactical and technical video analysis for your team or individual performance. Delivered within 4 days.",
      order: "Order an analysis",
      start: "Get started",
      how: "How it works",
      bookCall: "Book a call",
    },
    trustBadges: {
      delivery: "Delivery within 4 days",
      payment: "Secure payment",
      support: "WhatsApp support",
    },
    process: {
      eyebrow: "Process",
      title: "How it works",
      steps: [
        { id: "step-1", step: "01", icon: Play, title: "Send your video", desc: "Upload your match through SwissTransfer and share the link with us." },
        { id: "step-2", step: "02", icon: FileText, title: "Describe your needs", desc: "Tell us which tactical or technical aspects you want reviewed." },
        { id: "step-3", step: "03", icon: Send, title: "Receive your analysis", desc: "Your personalized analysis is delivered within 4 days maximum." },
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Our plans",
      popular: "Popular",
      subscribe: "Subscribe",
      choose: "Choose",
      cards: [
        {
          testId: "pricing-collective",
          title: "Team Match",
          description: "Tactical analysis for your team",
          price: 100,
          unit: "/ match",
          features: ["Complete tactical analysis", "Strengths and improvement areas", "Delivery within 4 days"],
        },
        {
          testId: "pricing-individual",
          title: "Individual Match",
          description: "Analysis of your personal performance",
          price: 100,
          unit: "/ match",
          features: ["Focus on your game", "Personalized advice", "Delivery within 4 days"],
        },
        {
          testId: "pricing-subscription",
          title: "Subscription",
          description: "For regular follow-up",
          price: 300,
          unit: "/ month",
          features: ["4 analyses per month", "Team or individual", "Priority support"],
          popular: true,
        },
      ],
    },
    trust: [
      { id: "trust-delai", icon: Clock, title: "Guaranteed timing", desc: "Delivery within 4 days maximum or refunded" },
      { id: "trust-paiement", icon: Shield, title: "Secure payment", desc: "Transactions protected by Stripe" },
      { id: "trust-support", icon: MessageCircle, title: "Responsive support", desc: "WhatsApp contact for any question" },
      { id: "trust-humain", icon: Users, title: "Human analysis", desc: "Real expertise, not generic AI output" },
    ],
    cta: {
      title: "Ready to improve?",
      subtitle: "Order your first analysis and discover the keys to your progress.",
      order: "Order now",
      create: "Create my account",
    },
  },
};

const useLandingCopy = () => {
  const { language } = useLanguage();
  return LANDING_COPY[language] || LANDING_COPY.fr;
};

export const HeroSection = ({ user }) => {
  const copy = useLandingCopy();

  return (
    <section
      className="min-h-[90vh] relative flex items-center justify-center"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(10,10,10,0.7), rgba(10,10,10,0.9)), url('https://images.unsplash.com/photo-1772707681004-ebbce15554d4?w=1920&q=80')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-center relative z-10">
        <div className="animate-fade-in">
          <span className="inline-block bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] mb-6">
            {copy.hero.badge}
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[1.1] text-white mb-6 animate-fade-in animate-delay-100">
          {copy.hero.titleTop}<br />
          <span className="text-[#CCFF00]">{copy.hero.titleAccent}</span>
        </h1>

        <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 animate-fade-in animate-delay-200">
          {copy.hero.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in animate-delay-300">
          <Link
            to={user ? "/pricing" : "/register"}
            className="bg-[#CCFF00] text-black px-8 py-4 font-bold uppercase tracking-wide text-sm hover:bg-[#AACC00] transition-all duration-150 active:scale-95 flex items-center gap-2"
            data-testid="hero-cta"
          >
            {user ? copy.hero.order : copy.hero.start}
            <ChevronRight className="w-4 h-4" />
          </Link>
          <a
            href="#comment-ca-marche"
            className="border border-white/20 text-white px-8 py-4 font-bold uppercase tracking-wide text-sm hover:border-white hover:bg-white/5 transition-all duration-150"
            data-testid="hero-secondary-cta"
          >
            {copy.hero.how}
          </a>
          <a
            href={CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-[#CCFF00]/40 text-[#CCFF00] px-8 py-4 font-bold uppercase tracking-wide text-sm hover:bg-[#CCFF00]/10 transition-all duration-150 flex items-center gap-2"
            data-testid="hero-call-cta"
          >
            <Video className="w-4 h-4" />
            {copy.hero.bookCall}
          </a>
        </div>

        <TrustBadges />
      </div>
    </section>
  );
};

const TrustBadges = () => {
  const copy = useLandingCopy();

  return (
    <div className="flex flex-wrap items-center justify-center gap-8 mt-16 animate-fade-in animate-delay-400">
      <div className="flex items-center gap-2 text-zinc-500 text-sm">
        <Clock className="w-4 h-4 text-[#CCFF00]" />
        <span>{copy.trustBadges.delivery}</span>
      </div>
      <div className="flex items-center gap-2 text-zinc-500 text-sm">
        <Shield className="w-4 h-4 text-[#CCFF00]" />
        <span>{copy.trustBadges.payment}</span>
      </div>
      <div className="flex items-center gap-2 text-zinc-500 text-sm">
        <MessageCircle className="w-4 h-4 text-[#CCFF00]" />
        <span>{copy.trustBadges.support}</span>
      </div>
    </div>
  );
};

export const HowItWorksSection = () => {
  const copy = useLandingCopy();

  return (
    <section id="comment-ca-marche" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">{copy.process.eyebrow}</span>
          <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mt-4 text-white">
            {copy.process.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {copy.process.steps.map((item) => (
            <div
              key={item.id}
              className="bg-[#18181B] border border-white/10 p-8 relative group hover:border-[#CCFF00]/30 transition-colors"
              data-testid={item.id}
            >
              <span className="text-6xl font-black text-[#CCFF00]/20 absolute top-4 right-4">{item.step}</span>
              <div className="w-12 h-12 bg-[#CCFF00]/10 flex items-center justify-center mb-6">
                <item.icon className="w-6 h-6 text-[#CCFF00]" />
              </div>
              <h3 className="text-xl font-bold uppercase mb-3 text-white">{item.title}</h3>
              <p className="text-zinc-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const PricingPreviewSection = ({ user }) => {
  const copy = useLandingCopy();

  return (
    <section className="py-24 md:py-32 bg-[#18181B]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">{copy.pricing.eyebrow}</span>
          <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mt-4 text-white">
            {copy.pricing.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {copy.pricing.cards.map((card) => (
            <PricingCard
              key={card.testId}
              {...card}
              user={user}
              popularLabel={copy.pricing.popular}
              subscribeLabel={copy.pricing.subscribe}
              chooseLabel={copy.pricing.choose}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingCard = ({ testId, title, description, price, unit, features, user, popular = false, popularLabel, subscribeLabel, chooseLabel }) => (
  <div
    className={`bg-[#0A0A0A] p-8 relative ${popular ? "border-2 border-[#CCFF00] glow" : "border border-white/10"}`}
    data-testid={testId}
  >
    {popular && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CCFF00] text-black text-xs font-bold uppercase px-3 py-1">
        {popularLabel}
      </span>
    )}
    <h3 className="text-lg font-bold uppercase text-white mb-2">{title}</h3>
    <p className="text-zinc-500 text-sm mb-6">{description}</p>
    <div className="mb-6">
      <span className="text-4xl font-black text-white">{price}€</span>
      <span className="text-zinc-500 ml-2">{unit}</span>
    </div>
    <ul className="space-y-3 mb-8">
      {features.map((feature) => (
        <li key={`${testId}-${feature}`} className="flex items-center gap-2 text-sm text-zinc-400">
          <CheckCircle className="w-4 h-4 text-[#CCFF00]" />
          {feature}
        </li>
      ))}
    </ul>
    <Link
      to={user ? "/pricing" : "/register"}
      className={`block w-full py-3 font-bold uppercase text-sm text-center transition-colors ${
        popular
          ? "bg-[#CCFF00] text-black hover:bg-[#AACC00]"
          : "border border-white/20 text-white hover:border-white hover:bg-white/5"
      }`}
      data-testid={`${testId}-cta`}
    >
      {popular ? subscribeLabel : chooseLabel}
    </Link>
  </div>
);

export const TrustSection = () => {
  const copy = useLandingCopy();

  return (
    <section className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {copy.trust.map((item) => (
            <div key={item.id} className="text-center" data-testid={item.id}>
              <div className="w-16 h-16 bg-[#CCFF00]/10 mx-auto flex items-center justify-center mb-4">
                <item.icon className="w-8 h-8 text-[#CCFF00]" />
              </div>
              <h3 className="text-lg font-bold uppercase text-white mb-2">{item.title}</h3>
              <p className="text-sm text-zinc-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CTASection = ({ user }) => {
  const copy = useLandingCopy();

  return (
    <section className="py-24 md:py-32 bg-[#CCFF00]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-black mb-6">
          {copy.cta.title}
        </h2>
        <p className="text-lg text-black/70 mb-10">
          {copy.cta.subtitle}
        </p>
        <Link
          to={user ? "/pricing" : "/register"}
          className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 font-bold uppercase tracking-wide text-sm hover:bg-zinc-900 transition-colors"
          data-testid="final-cta"
        >
          {user ? copy.cta.order : copy.cta.create}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};
